import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageTitle } from "@/components/SettingsPanel";
import { AdminEventReviews } from "@/components/AdminEventReviews";
import { StatusBadge, evTitle } from "@/components/EventCard";
import { api, errorText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { AppEvent, User } from "@/types";
import { useLive } from "@/lib/live";

export default function AdminEvents() {
  const { t, lang } = usePrefs();
  const me = useAuth().user as User;
  const isSuper = (me.admin_role || "super") === "super";
  const [events, setEvents] = useState<(AppEvent & { hidden_reason?: string })[]>([]);
  const load = () => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  };
  useLive(() => load());
  useEffect(load, []);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(t("admin.done"));
      load();
    } catch (e) {
      toast.error(errorText(e, t));
    }
  };
  const visibility = (e: AppEvent, action: "hide" | "unhide") => {
    const reason = window.prompt(t("admin.reasonRequired"));
    if (reason && reason.trim().length >= 3) run(() => api.post(`/admin/events/${e.event_id}/${action}`, { reason }));
  };


  return (
    <div data-testid="admin-events-page" className="space-y-10">
      <section>
        <PageTitle title={t("ae.events")} />
        <div className="grid gap-3">
          {events.map((e) => (
            <div key={e.event_id} data-testid={`admin-event-${e.event_id}`} className="rounded-3xl border bg-card p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{evTitle(e, lang)}</p>
                <p className="text-xs text-muted-foreground">{lang === "ar" ? e.venue.name_ar : e.venue.name_en} · {fmtDateTime(e.start_at, lang)}{e.hidden_reason && ` · ${t("status.reason")}: ${e.hidden_reason}`}</p>
              </div>
              {e.status === "hidden" ? <span className="rounded-full bg-zinc-700 text-white px-3 py-1 text-xs font-bold">{t("ae.hidden")}</span> : <StatusBadge status={e.request_status} />}
              {isSuper && (
                <button onClick={() => visibility(e, e.status === "hidden" ? "unhide" : "hide")} data-testid={`admin-event-visibility-${e.event_id}`} className="h-9 px-4 rounded-full border text-xs font-bold">
                  {t(e.status === "hidden" ? "ae.unhide" : "ae.hide")}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
      <AdminEventReviews canEdit={isSuper} />
    </div>
  );
}
